import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { getRulesErrorMessage } from './rolesErrors';

type MemberRole = 'owner' | 'organizer' | 'board' | 'va';

type EntityMember = {
  userId: string;
  role: MemberRole;
};

type PermissionDoc = {
  userId: string;
  page: string;
  feature: string;
  allowed: boolean;
};

const ROLE_LABELS: Record<MemberRole, string> = {
  owner: 'Organisator',
  organizer: 'Organisator',
  board: 'Bestuurslid',
  va: 'V,A'
};

interface AccessRolesSectionProps {
  entityId: string | null;
}

const AccessRolesSection: React.FC<AccessRolesSectionProps> = ({ entityId }) => {
  const [members, setMembers] = useState<EntityMember[]>([]);
  const [permissionsByUser, setPermissionsByUser] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const membersQuery = query(
          collection(db, 'entityMembers'),
          where('entityId', '==', entityId)
        );
        const membersSnap = await getDocs(membersQuery);
        const nextMembers = membersSnap.docs.map(docSnap => {
          const data = docSnap.data() as EntityMember;
          return {
            userId: data.userId,
            role: data.role
          };
        });

        const permissionsQuery = query(
          collection(db, 'permissions'),
          where('entityId', '==', entityId)
        );
        const permissionsSnap = await getDocs(permissionsQuery);
        const nextPermissions: Record<string, string[]> = {};
        permissionsSnap.docs.forEach(docSnap => {
          const data = docSnap.data() as PermissionDoc;
          if (!data.allowed) return;
          const label = `${data.page} · ${data.feature}`;
          if (!nextPermissions[data.userId]) {
            nextPermissions[data.userId] = [];
          }
          nextPermissions[data.userId].push(label);
        });

        setMembers(nextMembers);
        setPermissionsByUser(nextPermissions);
      } catch (err) {
        console.error('Error loading access roles:', err);
        setError(getRulesErrorMessage(err, 'Kan rollen en rechten niet laden.'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [entityId]);

  if (!entityId) {
    return <p className="text-sm text-gray-600">Selecteer een entiteit om rollen te bekijken.</p>;
  }

  if (loading) {
    return <p className="text-sm text-gray-600">Rollen en rechten laden...</p>;
  }

  if (error) {
    return <p className="text-sm text-[#4F6660]">{error}</p>;
  }

  if (members.length === 0) {
    return <p className="text-sm text-gray-600">Geen leden gevonden voor deze entiteit.</p>;
  }

  return (
    <div className="space-y-4">
      {members.map(member => (
        <div key={member.userId} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Gebruiker: {member.userId}</p>
              <p className="text-xs text-gray-600">Rol: {ROLE_LABELS[member.role]}</p>
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Rechten</p>
            {permissionsByUser[member.userId]?.length ? (
              <ul className="text-sm text-gray-700 space-y-1">
                {permissionsByUser[member.userId].map((permission, idx) => (
                  <li key={`${member.userId}-${idx}`}>{permission}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600">Geen expliciete rechten toegekend.</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AccessRolesSection;
